/**
 * SFOC's part of the sidebar: under the Model Viewer's Query card (ships and
 * date range, shared by every model), each picked ship's engine×fuel matrix,
 * where you select a single curve (cell), a whole engine (row header), or every
 * curve for the ship (the ship's master checkbox).
 *
 * It lives in the shared left panel rather than in a rail of its own, so the
 * sidebar, divider and chart stay where they are whichever model is showing.
 * Shown only on the SFOC model (`data-tab="modelviewer-sfoc"`).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  modelShips,
  sfocDate,
  sfocData,
  sfocSelected,
  sfocExpanded,
  sfocLoadingShips,
  sfocError,
  sfocLayout,
} from '../../state/store.ts';
import { shipLabel } from '../../utils/ships.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import '../../ui/layout-select.ts';
import type { ChartLayout } from '../../ui/layout-select.ts';
import { MEASURED_MODES, sfocMeasured, sfocTables } from './sfoc-measured.ts';
import {
  GENERIC_ENGINE,
  curveKey,
  getCurve,
  shipMatrix,
  keysForShip,
  keysForEngine,
  fuelVar,
  parseKey,
  triState,
  type TriState,
} from './sfoc-selection.ts';
import {
  ensureShipLoaded,
  toggleExpanded,
  toggleCurve,
  toggleKeys,
  clearAll,
} from './sfoc-data.ts';

@customElement('sfoc-curve-picker')
export class SfocCurvePicker extends LightElement {
  private store = new StoreController(this, [
    modelShips,
    sfocDate,
    sfocData,
    sfocSelected,
    sfocExpanded,
    sfocLoadingShips,
    sfocError,
    sfocMeasured,
    sfocTables,
    sfocLayout,
  ]);

  /**
   * One chart, or one panel per ship - offered once the selected curves span
   * more than one ship, as Explore offers Overlay / Grid only for several.
   * Each panel is the same chart, with the same colours, for its ship alone.
   */
  private renderLayout(): TemplateResult | typeof nothing {
    const ships = new Set(
      [...sfocSelected.value].map((k) => parseKey(k).ship).filter((s) => modelShips.value.includes(s)),
    );
    if (ships.size < 2) return nothing;
    return html`<layout-select
      data-tour="sfoc-layout"
      .value=${sfocLayout.value}
      .select=${(layout: ChartLayout) => {
        sfocLayout.set(layout);
        syncToUrl();
      }}
    ></layout-select>`;
  }

  /** Whether the Show card is folded, like Explore's Filters and Outliers. */
  @state() private showCollapsed = false;

  /**
   * What the chart area shows, as an Explore sidebar card: collapsible, a
   * segmented choice for the one-of-four measured layer (Explore's Overlay/Grid
   * switch) and a checkbox row per table (Explore's overlay toggles).
   */
  private renderShow(): TemplateResult {
    const mode = sfocMeasured.value;
    const tables = sfocTables.value;
    const shown = [
      mode === 'off' ? null : MEASURED_MODES.find((m) => m.id === mode)?.label.toLowerCase(),
      tables.values ? 'values' : null,
      tables.formulas ? 'formulas' : null,
    ].filter(Boolean);
    // The /ui whole-row switch (.dset-toggle): the line under the label says
    // what the current state means, not what the switch is.
    const table = (
      key: 'values' | 'formulas',
      label: string,
      on: string,
      off: string,
    ): TemplateResult => html`<button
      class="dset-toggle ${tables[key] ? 'is-on' : ''}"
      role="switch"
      aria-checked=${tables[key] ? 'true' : 'false'}
      @click=${() => sfocTables.set({ ...tables, [key]: !tables[key] })}
      data-tour=${`sfoc-${key}`}
    >
      <span class="dset-toggle-text">
        <strong>${label}</strong>
        <span>${tables[key] ? on : off}</span>
      </span>
      <span class="sd-switch" aria-hidden="true"></span>
    </button>`;
    return html`
      <div class="card sfoc-show-card">
        <div class="section-header" @click=${() => (this.showCollapsed = !this.showCollapsed)}>
          <span class="arrow ${this.showCollapsed ? '' : 'open'}">▶</span>
          <span>Show</span>
          <span class="count">${this.showCollapsed && shown.length ? `(${shown.join(', ')})` : ''}</span>
        </div>
        <div class="section-body ${this.showCollapsed ? 'collapsed' : ''}">
          ${this.renderLayout()}
          <div class="axis-block" data-tour="sfoc-measured">
            <label>Measured SFOC</label>
            <div class="seg" role="radiogroup" aria-label="Measured SFOC behind the curves">
              ${MEASURED_MODES.map(
                (m) => html`<button
                  class="seg-btn ${mode === m.id ? 'active' : ''}"
                  role="radio"
                  aria-checked=${mode === m.id ? 'true' : 'false'}
                  title=${m.title}
                  @click=${() => {
                    sfocMeasured.set(m.id);
                    syncToUrl();
                  }}
                >
                  ${m.label}
                </button>`,
              )}
            </div>
          </div>
          <div class="sfoc-show-tables">
            ${table(
              'values',
              'Values table',
              "Under the chart: each curve's SFOC at 30-90% load.",
              "Hidden. SFOC at 30-90% load, per curve.",
            )}
            ${table(
              'formulas',
              'Formulas table',
              "Under the chart: each curve's version, formula and R².",
              "Hidden. Each curve's version, formula and R².",
            )}
          </div>
        </div>
      </div>
    `;
  }

  override render(): TemplateResult {
    const picked = modelShips.value;
    const selectedCount = [...sfocSelected.value].filter((k) =>
      picked.includes(parseKey(k).ship),
    ).length;
    return html`
      <div class="card sfoc-curves-card" data-tour="sfoc-curves">
        <h2>
          Curves
          ${selectedCount ? html`<span class="sfoc-count">${selectedCount}</span>` : nothing}
          ${selectedCount
            ? html`<button class="sfoc-clear" title="Deselect every curve" @click=${clearAll}>
                Clear
              </button>`
            : nothing}
        </h2>
        <p
          class="sfoc-curve-date"
          title="Every version up to the range's last day is loaded; the chart draws the one in force at the time slicer's moment"
        >
          Versions up to <strong>${sfocDate.value}</strong>, the last day of the date range
        </p>
        ${picked.length
          ? html`<div class="sfoc-ship-list">${picked.map((ship) => this.renderShip(ship))}</div>`
          : html`<div class="sfoc-hint">Pick ships above to list their engines and fuels.</div>`}
        ${sfocError.value ? html`<div class="sfoc-err">${sfocError.value}</div>` : nothing}
      </div>
      ${this.renderShow()}
    `;
  }

  private renderShip(ship: string): TemplateResult {
    const expanded = sfocExpanded.value.has(ship);
    const loading = sfocLoadingShips.value.has(ship);
    const sd = sfocData.value[ship];
    const selected = sfocSelected.value;
    const master: TriState = sd ? triState(keysForShip(sd, ship), selected) : 'none';

    return html`
      <div class="sfoc-ship ${expanded ? 'expanded' : ''}">
        <div class="sfoc-ship-head">
          <button
            class="sfoc-check ${master}"
            title="Toggle all curves for ${shipLabel(ship)}"
            @click=${() => this.onShipMaster(ship)}
          ></button>
          <button
            class="sfoc-ship-name"
            aria-expanded=${expanded}
            title=${ship}
            @click=${() => toggleExpanded(ship)}
          >
            <span class="sfoc-caret">${expanded ? '▾' : '▸'}</span>
            <span>${shipLabel(ship)}</span>
          </button>
        </div>
        ${expanded
          ? html`<div class="sfoc-ship-body">
              ${loading && !sd
                ? html`<div class="sfoc-loading"><span class="spinner spinner-sm" aria-hidden="true"></span> Loading…</div>`
                : sd
                  ? this.renderMatrix(ship)
                  : html`<div class="sfoc-hint">No curves.</div>`}
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderMatrix(ship: string): TemplateResult {
    const sd = sfocData.value[ship]!;
    const selected = sfocSelected.value;
    const m = shipMatrix(sd);
    if (m.fuels.length === 0) return html`<div class="sfoc-hint">No curves for this date.</div>`;

    // One row per engine. The cell columns are the ship's union of fuels (so rows
    // align), but each cell self-labels with its fuel name and grays out when that
    // engine can't burn it - no separate header row.
    const cols = `minmax(2.4rem, auto) repeat(${m.fuels.length}, minmax(0, 1fr))`;
    return html`
      <div class="sfoc-matrix" style="grid-template-columns:${cols}">
        ${m.rows.map((row) => {
          const rowState = triState(keysForEngine(sd, ship, row.engine), selected);
          return html`
            <button
              class="sfoc-mx-row ${rowState}"
              title="Toggle all fuels for ${row.engine}"
              @click=${() => toggleKeys(keysForEngine(sd, ship, row.engine))}
            >
              ${row.engine === GENERIC_ENGINE ? 'All' : row.engine}
            </button>
            ${m.fuels.map((fuel) => {
              if (!row.fuels.has(fuel)) {
                return html`<span class="sfoc-cell na" title="${fuel} - not available on ${row.engine}"
                  >${fuel}</span
                >`;
              }
              const key = curveKey(ship, row.engine, fuel);
              const on = selected.has(key);
              const generic = getCurve(sd, row.engine, fuel)?.generic ?? false;
              const token = fuelVar(fuel);
              return html`<button
                class="sfoc-cell ${on ? 'on' : ''} ${generic ? 'generic' : ''} ${token ? 'fuel' : ''}"
                style=${token ? `--fuel: var(${token})` : nothing}
                title="${ship} · ${row.engine} · ${fuel}${generic ? ' - generic fallback curve' : ''}"
                @click=${() => toggleCurve(key)}
              >
                ${fuel}
              </button>`;
            })}
          `;
        })}
      </div>
    `;
  }

  private async onShipMaster(ship: string): Promise<void> {
    await ensureShipLoaded(ship);
    const sd = sfocData.value[ship];
    if (!sd) return;
    if (!sfocExpanded.value.has(ship)) toggleExpanded(ship);
    toggleKeys(keysForShip(sd, ship));
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'sfoc-curve-picker': SfocCurvePicker;
  }
}
